import { FunctionComponent, ReactNode } from "react";

import { REVOCATION_BUTTON_URL } from "../../../utils/constants";

interface RevocationProps {}

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section: FunctionComponent<SectionProps> = ({ title, children }) => {
  return (
    <section className="flex w-full flex-col gap-4">
      <h2 className="font-GeistBold text-xl">{title}</h2>
      {children}
    </section>
  );
};

const Revocation: FunctionComponent<RevocationProps> = () => {
  return (
    <main className="mx-auto my-[85px] flex w-full flex-col items-start justify-start px-4 pb-16 lg:w-1/3 lg:px-0">
      <header className="w-full text-left">
        <h1 className="font-GeistBold text-3xl">
          Widerrufsrecht für den Verkauf von Waren
        </h1>
      </header>

      <hr className="my-6 h-[1px] w-full border-0 bg-slate-300" />

      <div className="flex w-full flex-col gap-10 text-base leading-relaxed">
        <section className="relative flex w-full items-start justify-start overflow-hidden rounded-xl border-2 border-blue-900 bg-gray-50 px-6 py-5 shadow-lg">
          <span
            aria-hidden="true"
            className="absolute left-0 top-0 h-full w-3 bg-blue-900"
          />

          <div className="flex flex-col gap-2 pl-2 text-gray-800">
            <h2 className="font-GeistBold text-lg">
              Widerrufsrecht für Verbraucher
            </h2>

            <p>
              (Verbraucher ist jede natürliche Person, die ein Rechtsgeschäft zu
              Zwecken abschließt, die überwiegend weder ihrer gewerblichen noch
              ihrer selbstständigen beruflichen Tätigkeit zugerechnet werden
              können.)
            </p>
          </div>
        </section>

        <section className="flex w-full flex-col gap-8">
          <h2 className="font-GeistBold text-2xl">Widerrufsbelehrung</h2>

          <Section title="Widerrufsrecht">
            <p>
              Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen
              diesen Vertrag zu widerrufen.
            </p>

            <p>Die Widerrufsfrist beträgt 14 Tage ab dem Tag,</p>

            <ul className="list-disc pl-6">
              <li>
                an dem Sie oder ein von Ihnen benannter Dritter, der nicht der
                Beförderer ist, die Waren in Besitz genommen haben bzw. hat,
                sofern Sie eine oder mehrere Waren im Rahmen einer einheitlichen
                Bestellung bestellt haben und diese einheitlich geliefert wird
                bzw. werden;
              </li>
            </ul>

            <p>
              Um Ihr Widerrufsrecht auszuüben, müssen Sie uns (Juwelier Bucher
              GbR, Weinholdstraße 33, 08468 Reichenbach, Telefonnr.:{" "}
              <a
                href="tel:+49376512802"
                className="underline underline-offset-2"
              >
                03765 12802
              </a>
              , E-Mail-Adresse:{" "}
              <a
                href="mailto:info@juwelier-bucher.de"
                className="underline underline-offset-2"
              >
                info@juwelier-bucher.de
              </a>
              ) mittels einer eindeutigen Erklärung (z. B. ein mit der Post
              versandter Brief oder eine E-Mail) über Ihren Entschluss, diesen
              Vertrag zu widerrufen, informieren. Sie können dafür das
              beigefügte Muster-Widerrufsformular verwenden, das jedoch nicht
              vorgeschrieben ist.
            </p>

            <p>
              Sie können Ihr Widerrufsrecht auch online unter einer entsprechend
              bezeichneten Schaltfläche (&quot;Vertrag widerrufen&quot; oder
              ähnliche Bezeichnung) auf unserer Webseite{" "}
              <a
                href="https://www.juwelier-bucher.de"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                www.juwelier-bucher.de
              </a>{" "}
              ausüben.
            </p>

            <p>
              Wenn Sie diese Online-Funktion nutzen, übermitteln wir Ihnen auf
              einem dauerhaften Datenträger (z. B. durch eine E-Mail)
              unverzüglich eine Eingangsbestätigung mit Informationen zum Inhalt
              der Widerrufserklärung sowie dem Datum und der Uhrzeit ihres
              Eingangs.
            </p>

            <a
              href={REVOCATION_BUTTON_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-max rounded-lg border border-gray-900 bg-gray-200 px-5 py-2.5 font-GeistBold text-gray-900 underline-offset-2 hover:underline"
            >
              Vertrag widerrufen
            </a>

            <p>
              Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die
              Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf der
              Widerrufsfrist absenden.
            </p>
          </Section>

          <Section title="Folgen des Widerrufs">
            <p>
              Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle
              Zahlungen, die wir von Ihnen erhalten haben, einschließlich der
              Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die sich
              daraus ergeben, dass Sie eine andere Art der Lieferung als die von
              uns angebotene, günstigste Standardlieferung gewählt haben),
              unverzüglich und spätestens binnen 14 Tagen ab dem Tag
              zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses
              Vertrags bei uns eingegangen ist.
            </p>

            <p>
              Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das
              Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei
              denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; in
              keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte
              berechnet.
            </p>

            <p>
              Wir können die Rückzahlung verweigern, bis wir die Waren wieder
              zurückerhalten haben oder bis Sie den Nachweis erbracht haben,
              dass Sie die Waren zurückgesandt haben, je nachdem, welches der
              frühere Zeitpunkt ist.
            </p>

            <p>
              Sie haben die Waren unverzüglich und in jedem Fall spätestens
              binnen 14 Tagen ab dem Tag, an dem Sie uns über den Widerruf
              dieses Vertrags unterrichten, an uns zurückzusenden oder zu
              übergeben. Die Frist ist gewahrt, wenn Sie die Waren vor Ablauf
              der Frist von 14 Tagen absenden.
            </p>

            <p>
              Sie tragen die unmittelbaren Kosten der Rücksendung der Waren.
            </p>

            <p>
              Sie müssen für einen etwaigen Wertverlust der Waren nur aufkommen,
              wenn dieser Wertverlust auf einen zur Prüfung der Beschaffenheit,
              Eigenschaften und Funktionsweise der Waren nicht notwendigen
              Umgang mit ihnen zurückzuführen ist.
            </p>
          </Section>

          <section className="flex w-full flex-col gap-6">
            <h2 className="font-GeistBold text-xl">
              Ausschluss- bzw. Erlöschensgründe
            </h2>

            <section className="flex flex-col gap-4">
              <h3 className="font-GeistBold text-lg">
                Das Widerrufsrecht besteht nicht bei Verträgen
              </h3>

              <ul className="list-disc space-y-3 pl-6">
                <li>
                  zur Lieferung von Waren, die nicht vorgefertigt sind und für
                  deren Herstellung eine individuelle Auswahl oder Bestimmung
                  durch den Verbraucher maßgeblich ist oder die eindeutig auf
                  die persönlichen Bedürfnisse des Verbrauchers zugeschnitten
                  sind;
                </li>

                <li>
                  zur Lieferung von Waren, die schnell verderben können oder
                  deren Verfallsdatum schnell überschritten würde;
                </li>

                <li>
                  zur Lieferung alkoholischer Getränke, deren Preis bei
                  Vertragsschluss vereinbart wurde, die aber frühestens 30 Tage
                  nach Vertragsschluss geliefert werden können und deren
                  aktueller Wert von Schwankungen auf dem Markt abhängt, auf die
                  der Unternehmer keinen Einfluss hat;
                </li>

                <li>
                  zur Lieferung von Zeitungen, Zeitschriften oder Illustrierten
                  mit Ausnahme von Abonnement-Verträgen.
                </li>
              </ul>
            </section>

            <section className="flex flex-col gap-4">
              <h3 className="font-GeistBold text-lg">
                Das Widerrufsrecht erlischt vorzeitig bei Verträgen
              </h3>

              <ul className="list-disc space-y-3 pl-6">
                <li>
                  zur Lieferung versiegelter Waren, die aus Gründen des
                  Gesundheitsschutzes oder der Hygiene nicht zur Rückgabe
                  geeignet sind, wenn ihre Versiegelung nach der Lieferung
                  entfernt wurde;
                </li>

                <li>
                  zur Lieferung von Waren, wenn diese nach der Lieferung
                  aufgrund ihrer Beschaffenheit untrennbar mit anderen Gütern
                  vermischt wurden;
                </li>

                <li>
                  zur Lieferung von Ton- oder Videoaufnahmen oder
                  Computersoftware in einer versiegelten Packung, wenn die
                  Versiegelung nach der Lieferung entfernt wurde.
                </li>
              </ul>
            </section>
          </section>
        </section>

        <hr className="h-[1px] w-full border-0 bg-slate-300" />

        <section className="flex w-full flex-col gap-4">
          <h2 className="font-GeistBold text-2xl">Muster-Widerrufsformular</h2>

          <p>
            (Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte
            dieses Formular aus und senden Sie es zurück.)
          </p>

          <div className="flex w-full flex-col gap-8 rounded-md border border-gray-400 p-6 sm:p-8">
            <div className="flex flex-col gap-3">
              <p className="font-GeistBold">
                An Juwelier Bucher GbR, Weinholdstraße 33, 08468 Reichenbach,
                E-Mail-Adresse:{" "}
                <a
                  href="mailto:info@juwelier-bucher.de"
                  className="underline underline-offset-2"
                >
                  info@juwelier-bucher.de
                </a>
                :
              </p>
            </div>

            <p>
              Hiermit widerrufe(n) ich/ wir (*) den von mir/ uns (*)
              abgeschlossenen Vertrag über den Kauf der folgenden Waren (*)/ die
              Erbringung der folgenden Dienstleistung (*)
            </p>

            <div className="min-h-20 border-b border-gray-400" />

            <div className="flex flex-col gap-2">
              <p>Bestellt am (*)/ erhalten am (*)</p>
              <div className="h-10 border-b border-gray-400" />
            </div>

            <div className="flex flex-col gap-2">
              <p>Name des/ der Verbraucher(s)</p>
              <div className="h-10 border-b border-gray-400" />
            </div>

            <div className="flex flex-col gap-2">
              <p>Anschrift des/ der Verbraucher(s)</p>
              <div className="min-h-16 border-b border-gray-400" />
            </div>

            <div className="grid gap-10 pt-16 sm:grid-cols-3">
              <div className="flex flex-col gap-2 sm:col-span-2">
                <div className="border-b border-black" />

                <p className="text-sm">
                  Unterschrift des/ der Verbraucher(s) (nur bei Mitteilung auf
                  Papier)
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <div className="border-b border-black" />
                <p className="text-sm">Datum</p>
              </div>
            </div>

            <p className="rounded-md bg-blue-100 p-2">
              (*) Unzutreffendes streichen.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Revocation;
